import { Css, Icon, Palette, useTestIds } from "@homebound/beam";
import { useEffect, useMemo } from "react";
import { CommitmentStatus, CommunityType, LotType } from "src/generated/graphqlTypes";
import { ConfirmationModal } from "src/routes/components/ConfirmationModal";
import { isDefined, queryResult } from "src/utils";
import { formatCentsToPrice } from "src/utils/numbers";
import {
  ApplyInitialConfigurationGate_StructureFragment,
  useApplyInitialConfigurationGateQuery,
} from "./ApplyInitialConfigurationGate.generated";

type ApplyInitialConfigurationGateProps = {
  projectId: string;
  onContinue: () => void;
  onChooseAnotherWorkflow: () => void;
};

/**
 * Gates the Apply Initial Configuration wizard on the project's eligibility, which only has something to
 * say when the project has POs that might impact the config appliance. An eligible project therefore has
 * nothing to acknowledge and goes straight into the wizard.
 */
export function ApplyInitialConfigurationGate(props: ApplyInitialConfigurationGateProps) {
  const query = useApplyInitialConfigurationGateQuery({ variables: { projectId: props.projectId } });
  return queryResult(query, (data) => <ApplyInitialConfigurationGateView project={data.structure} {...props} />);
}

type ApplyInitialConfigurationGateViewProps = Omit<ApplyInitialConfigurationGateProps, "projectId"> & {
  project: ApplyInitialConfigurationGate_StructureFragment;
};
function ApplyInitialConfigurationGateView(props: ApplyInitialConfigurationGateViewProps) {
  const { project, onContinue, onChooseAnotherWorkflow } = props;

  const { isBoolSuburban, hasConfiguration, hasStartedConstruction, voidablePos, nonVoidablePos } = useMemo(() => {
    // Filter out precon POs (e.g. surveys, engineering) that are committed before the configuration is applied
    const [voidablePos, nonVoidablePos] = project.commitments
      .filter((c) => !c.isPrecon && ![CommitmentStatus.Voided, CommitmentStatus.Draft].includes(c.status))
      .partition((c) => c.canBeVoided.allowed);
    return {
      voidablePos,
      nonVoidablePos,
      hasStartedConstruction: project.hasStartedConstruction,
      isBoolSuburban: project.lotType.code === LotType.Bool && project.communityType?.code === CommunityType.Suburban,
      hasConfiguration: isDefined(project.readyPlanConfig),
    };
  }, [project]);

  // Voidable POs don't block the workflow, they just need an extra heads-up before proceed
  const isBlocked = hasStartedConstruction || nonVoidablePos.nonEmpty || (isBoolSuburban && !hasConfiguration);
  const isEligible = !isBlocked && voidablePos.isEmpty;

  // Because we need to query the project info to figure out if this gate applies.
  // This useEffect is rendering the case where it doesn't.
  // TODO: Re-work this using hook / BE field to skip the gate rendering on CreateGuidedChangeEventModal & remove this
  useEffect(() => {
    if (isEligible) onContinue();
  }, [isEligible, onContinue]);

  if (isBlocked) {
    return (
      <IneligibleModal
        onConfirm={onChooseAnotherWorkflow}
        hasStartedConstruction={hasStartedConstruction}
        nonVoidablePos={nonVoidablePos}
        hasConfiguration={hasConfiguration}
        isBoolSuburban={isBoolSuburban}
      />
    );
  } else if (isEligible) {
    // The effect above is already entering the wizard, which closes this modal.
    return null;
  } else {
    return <EligibleWarningModal voidablePosCount={voidablePos.length} onConfirm={onContinue} />;
  }
}

function EligibleWarningModal(props: { voidablePosCount: number; onConfirm: () => void }) {
  const { voidablePosCount, onConfirm } = props;
  const tid = useTestIds(props, "gate");
  return (
    <ConfirmationModal
      title="Purchase Orders May Need to be Voided"
      label="Continue"
      onConfirmAction={onConfirm}
      icon={{ icon: "error", color: Palette.Orange600 }}
      confirmationMessage={
        <>
          <p css={Css.sm.gray900.mb3.$}>
            {voidablePosCount} purchase orders are tied to the current building scope. They can be voided, so you can
            still apply the Initial Configuration. You'll review which of them may need to be voided before applying.
          </p>
          <EligibilityCheck
            type="warn"
            label={`${voidablePosCount} Voidable POs`}
            tooltip="POs that haven't been billed against yet can be voided, so the configuration can still be replaced."
            description="These POs may need to be voided."
            {...tid.check}
          />
        </>
      }
    />
  );
}

function IneligibleModal(props: {
  onConfirm: () => void;
  hasStartedConstruction: boolean;
  nonVoidablePos: ApplyInitialConfigurationGate_StructureFragment["commitments"];
  hasConfiguration: boolean;
  isBoolSuburban: boolean;
}) {
  const { onConfirm, hasStartedConstruction, nonVoidablePos, hasConfiguration, isBoolSuburban } = props;
  const tid = useTestIds(props, "gate");
  return (
    <ConfirmationModal
      title="This project isn't eligible for Initial Configuration"
      label="Choose Another Workflow"
      onConfirmAction={onConfirm}
      closeOnConfirm={false}
      confirmationMessage={
        <>
          <p css={Css.sm.gray900.mb3.$}>
            This project has purchase orders that can't be voided. Choose another Change Event workflow to modify the
            existing configuration.
          </p>
          <p css={Css.smSb.gray900.mb4.$} {...tid.whyThisWorkflowIsntAvailable}>
            Why this workflow isn't available:
          </p>
          <div css={Css.df.fdc.gap3.$}>
            <EligibilityCheck
              type={hasStartedConstruction ? "warn" : "pass"}
              label="Before Ready-to-Start Milestone"
              tooltip="An Initial Configuration can only be applied before the project passes its Ready-to-Start date."
              description={
                hasStartedConstruction
                  ? "This project has already passed its Ready-to-Start date."
                  : "The project has not completed the RTS milestones."
              }
              {...tid.check}
            />
            {isBoolSuburban && (
              <EligibilityCheck
                type={hasConfiguration ? "pass" : "warn"}
                label="Existing Configuration"
                tooltip="Initial Configuration requires an existing configuration to replace, except for eligible Suburban projects."
                description="This project doesn’t have an existing configuration."
                {...tid.check}
              />
            )}
            <EligibilityCheck
              type={nonVoidablePos.nonEmpty ? "warn" : "pass"}
              label="Non-Voidable Purchase Orders"
              tooltip="This workflow is only available before building scope has been committed. Existing pre-con work, such as surveys, geotechnical work, and permits, does not prevent this workflow."
              description={
                nonVoidablePos.nonEmpty
                  ? `${nonVoidablePos.length} POs totaling ${formatCentsToPrice(
                      nonVoidablePos.sum((po) => po.committedInCents),
                      { rounded: true, trim: true },
                    )} can't be voided.`
                  : "This project has no purchase orders that block the change."
              }
              {...tid.check}
            />
          </div>
        </>
      }
    />
  );
}

type EligibilityCheckProps = {
  type: "pass" | "warn";
  label: string;
  tooltip: string;
  description: string;
  "data-testid"?: string;
};
function EligibilityCheck(props: EligibilityCheckProps) {
  const { type, label, tooltip, description } = props;
  const isPass = type === "pass";
  const tid = useTestIds(props, "criterion");

  return (
    <div css={Css.df.aifs.gap2.$} {...tid}>
      <Icon icon={isPass ? "checkCircle" : "error"} color={isPass ? Palette.Green500 : Palette.Orange600} />
      <div css={Css.df.fdc.gapPx(4).$}>
        <span css={Css.df.aic.gapPx(12).smSb.gray900.$}>
          {label}
          <Icon icon="infoCircle" inc={2} color={Palette.Gray600} tooltip={tooltip} />
        </span>
        <span css={Css.sm.gray900.$}>{description}</span>
      </div>
    </div>
  );
}
